import React, { useEffect, useState } from 'react';
import { Button, InputNumber, Modal, Slider } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { wrapSegments } from '../model/greenWave';
import { TlGreenWaveIcon } from './mapIcons';
import { MODAL_Z_INDEX } from './modalLayer';

export function SyncfixModeBar({ count, onCalculate, onCancel }) {
  return (
    <div className="semafix-banner semafix-banner-syncfix">
      <img className="semafix-banner-icon" src={TlGreenWaveIcon} alt="" />
      <span>Modalità SYNCFIX · seleziona almeno 2 intersezioni semaforizzate</span>
      <b className="semafix-banner-count">{count}</b>
      <button className="semafix-banner-cta" onClick={onCalculate} disabled={count < 2}>
        Calcola offset
      </button>
      <button className="semafix-banner-ghost" onClick={onCancel}>Annulla</button>
    </div>
  );
}

/**
 * Confirms the selected signals before the offset is calculated: plan status,
 * timings, crossing length, and the distance and speed of each segment.
 */
export function SyncfixConfigModal({
  open,
  stops = [],
  distances = [],
  speeds = [],
  optimizedIds = {},
  onOptimize,
  onCancel,
  onConfirm,
}) {
  const [draftDistances, setDraftDistances] = useState(distances);
  const [draftSpeeds, setDraftSpeeds] = useState(speeds);
  const [draftStops, setDraftStops] = useState(stops);

  useEffect(() => {
    if (open) {
      setDraftDistances(distances);
      setDraftSpeeds(speeds);
      setDraftStops(stops.map((stop) => ({ ...stop, startGreen: Number(stop.startGreen || 0) })));
    }
  }, [open, distances, speeds, stops]);

  const updateStop = (id, field, value) => {
    setDraftStops((current) => current.map((stop) => (
      stop.id === id ? { ...stop, [field]: Number(value || 0) } : stop
    )));
  };

  return (
    <Modal
      open={open}
      title="Configura Semafori"
      onCancel={onCancel}
      width={920}
      zIndex={MODAL_Z_INDEX}
      className="semafix-modal semafix-syncfix-config"
      footer={[
        <Button key="cancel" onClick={onCancel}>Annulla</Button>,
        <Button key="ok" type="primary" disabled={stops.length < 2} onClick={() => onConfirm({
          distances: draftDistances,
          speeds: draftSpeeds,
          stops: draftStops,
        })}
        >
          Calcola l&apos;offset
        </Button>,
      ]}
    >
      <div className="semafix-syncfix-section">Informazioni semafori</div>
      <div className="semafix-editor-table-wrap">
        <table className="semafix-editor-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Stato ottimizzazione</th>
              <th>Inizio verde</th>
              <th>Verde</th>
              <th>Giallo</th>
              <th>Rosso</th>
              <th>Lungh. attr.</th>
              <th>Azione</th>
            </tr>
          </thead>
          <tbody>
            {draftStops.map((stop) => {
              const optimized = Boolean(optimizedIds[stop.id]);
              return (
                <tr key={stop.id}>
                  <td>{stop.name}</td>
                  <td>
                    <span className={`semafix-syncfix-status${optimized ? ' is-ready' : ''}`}>
                      {optimized ? 'Ottimizzato' : 'Non ottimizzato'}
                    </span>
                  </td>
                  <td><InputNumber min={0} value={stop.startGreen} onChange={(value) => updateStop(stop.id, 'startGreen', value)} /></td>
                  <td><InputNumber min={1} value={stop.green} onChange={(value) => updateStop(stop.id, 'green', value)} /></td>
                  <td><InputNumber min={0} value={stop.yellow} onChange={(value) => updateStop(stop.id, 'yellow', value)} /></td>
                  <td><InputNumber min={1} value={stop.red} onChange={(value) => updateStop(stop.id, 'red', value)} /></td>
                  <td>
                    <InputNumber
                      min={0}
                      value={stop.crossingLength}
                      placeholder="—"
                      onChange={(value) => updateStop(stop.id, 'crossingLength', value)}
                    />
                  </td>
                  <td>
                    <Button size="small" onClick={() => onOptimize?.(stop.id)}>
                      {optimized ? 'Riottimizza piano' : 'Ottimizza piano'}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="semafix-syncfix-section">Informazioni tratte</div>
      <div className="semafix-editor-table-wrap">
        <table className="semafix-editor-table">
          <thead>
            <tr>
              <th>Tratta</th>
              <th>Distanza [m]</th>
              <th>Velocità [km/h]</th>
            </tr>
          </thead>
          <tbody>
            {stops.slice(1).map((stop, index) => (
              <tr key={`${stops[index].id}-${stop.id}`}>
                <td>{stops[index].name} → {stop.name}</td>
                <td>
                  <InputNumber
                    min={1}
                    value={draftDistances[index]}
                    onChange={(value) => setDraftDistances((current) => current.map((item, itemIndex) => (
                      itemIndex === index ? Number(value || item) : item
                    )))}
                  />
                </td>
                <td>
                  <InputNumber
                    min={5}
                    max={130}
                    value={draftSpeeds[index]}
                    onChange={(value) => setDraftSpeeds((current) => current.map((item, itemIndex) => (
                      itemIndex === index ? Number(value || item) : item
                    )))}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

function PhaseTrack({ row, cycle }) {
  const toStyle = ({ start, length }) => ({
    left: `${(start / cycle) * 100}%`,
    width: `${(length / cycle) * 100}%`,
  });
  return (
    <div className="semafix-gw-track">
      {wrapSegments(row.offset, row.green, cycle).map((segment, index) => (
        <i key={`g${index}`} className="green" style={toStyle(segment)} />
      ))}
      {wrapSegments(row.offset + row.green, row.yellow, cycle).map((segment, index) => (
        <i key={`y${index}`} className="yellow" style={toStyle(segment)} />
      ))}
    </div>
  );
}

export function GreenWaveCard({
  wave,
  onSpeedChange,
  onOpenSyncfixModule,
  canOpenSyncfixModule,
  onOptimizeMissing,
  onClose,
}) {
  const [simulationOpen, setSimulationOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!playing || !wave) return undefined;
    const timer = window.setInterval(() => {
      setElapsed((current) => (current + 0.5) % Math.max(1, wave.commonCycle));
    }, 500);
    return () => window.clearInterval(timer);
  }, [playing, wave]);
  if (!wave) return null;
  const { commonCycle, greenBand, rows } = wave;
  const cumulativeDistances = rows.reduce((result, row, index) => {
    result.push((result[index - 1] || 0) + (index ? row.distance : 0));
    return result;
  }, []);
  const totalDistance = Math.max(1, cumulativeDistances[cumulativeDistances.length - 1] || wave.totalDistance);

  return (
    <div className="semafix-gw-card">
      <button className="semafix-popup-close" onClick={onClose} aria-label="Chiudi">
        <CloseOutlined />
      </button>
      <div className="semafix-gw-head">
        <img src={TlGreenWaveIcon} alt="" />
        <div>
          <div className="semafix-popup-title">SYNCFIX · Onda verde</div>
          <div className="semafix-popup-subtitle">
            <span className="semafix-dot" />
            {rows.length} IMPIANTI COORDINATI · {wave.totalDistance} M
          </div>
        </div>
      </div>

      <div className="semafix-kpis">
        <div><b>{commonCycle}s</b><span>Ciclo comune</span></div>
        <div><b>{Number.isFinite(greenBand) ? `${greenBand}s` : '—'}</b><span>Banda verde</span></div>
        <div><b>{wave.speedKph}</b><span>Progressione km/h</span></div>
        <div><b>{wave.directionLabel}</b><span>Direzione prioritaria</span></div>
      </div>

      <div className="semafix-gw-speed">
        <span>Velocità di progressione</span>
        <Slider
          min={20}
          max={70}
          step={5}
          value={wave.speedKph}
          onChange={onSpeedChange}
          tooltip={{ formatter: (value) => `${value} km/h` }}
        />
      </div>

      <div className="semafix-sect">Distanze e velocità</div>
      <div className="semafix-gw-segments">
        {rows.slice(1).map((row, index) => (
          <div key={`${rows[index].id}-${row.id}`}>
            <span>{rows[index].name}</span>
            <b>→</b>
            <span>{row.name}</span>
            <strong>{row.distance} m</strong>
            <em>{Math.round(row.distance / Math.max(1, row.travelTime) * 3.6)} km/h</em>
          </div>
        ))}
      </div>

      <div className="semafix-sect">Dettaglio semafori</div>
      <div className="semafix-gw-signal-cards">
        {rows.map((row, index) => (
          <article key={row.id}>
            <header><img src={TlGreenWaveIcon} alt="" /><b>{row.name || `Semaforo ${index + 1}`}</b></header>
            <div>
              <span>Offset calcolato<b>{row.offset.toFixed(2)}s</b></span>
              <span>Nuovo inizio verde<b>{row.offset.toFixed(2)}s</b></span>
            </div>
            <footer>
              <span>Verde <b className="green">{row.green}s</b></span>
              <span>Giallo <b className="yellow">{row.yellow}s</b></span>
              <span>Rosso <b className="red">{row.red}s</b></span>
            </footer>
          </article>
        ))}
      </div>

      <div className="semafix-sect">Diagramma spazio-tempo</div>
      <div className="semafix-gw-ruler">
        <span>0s</span>
        <span>{Math.round(commonCycle / 2)}s</span>
        <span>{commonCycle}s</span>
      </div>
      <div className="semafix-gw-rows">
        {rows.map((row) => (
          <div className="semafix-gw-row" key={row.id}>
            <span className="semafix-gw-label" title={`${row.name} · ${row.maneuverTitle || 'parametri manuali'}`}>
              {row.name}
              <small>{row.maneuverTitle || 'Manovra non associata'} · {row.timingSource === 'latest-optimized-plan' ? 'ultimo piano ottimizzato' : 'parametri manuali/catalogo'}</small>
            </span>
            <PhaseTrack row={row} cycle={commonCycle} />
            <b className="semafix-gw-offset">{row.isReference ? 'rif.' : `+${row.offset}s`}</b>
          </div>
        ))}
      </div>

      <div className="semafix-gw-note">
        Offset calcolati su distanza tra impianti, velocità di progressione e posizione della fase di
        verde; l’ordine segue la direzione a flusso prevalente ({wave.priorityFlow} v/h).
      </div>

      {rows.some((row) => row.timingSource !== 'latest-optimized-plan') && (
        <div className="semafix-gw-missing-plan">
          <span>
            Alcuni impianti non hanno ancora un piano ottimizzato: puoi usare i parametri
            manuali/catalogo oppure aprire l’editor.
          </span>
          <Button size="small" onClick={onOptimizeMissing}>Ottimizza il primo mancante</Button>
        </div>
      )}

      {canOpenSyncfixModule && (
        <div className="semafix-popup-actions">
          <Button size="small" onClick={() => setSimulationOpen(true)}>
            Simula onda verde
          </Button>
          <Button type="primary" size="small" onClick={onOpenSyncfixModule}>
            Apri corridoio in SYNCFIX
          </Button>
        </div>
      )}

      <Modal
        open={simulationOpen}
        onCancel={() => { setSimulationOpen(false); setPlaying(false); }}
        footer={null}
        width="calc(100vw - 48px)"
        zIndex={MODAL_Z_INDEX}
        className="semafix-modal semafix-gw-simulation"
        title={(
          <div>
            <b>Simulazione Onda verde su direttrice</b>
            <small>Velocità: {wave.speedKph} km/h</small>
          </div>
        )}
      >
        <div className="semafix-simulation-time">{elapsed.toFixed(1)}s <span>/ {commonCycle}s</span></div>
        <button
          type="button"
          className="semafix-simulation-play"
          onClick={() => setPlaying((current) => !current)}
          aria-label={playing ? 'Pausa simulazione' : 'Avvia simulazione'}
        >
          {playing ? 'Ⅱ' : '▶'}
        </button>
        <div className="semafix-simulation-track">
          <div className="semafix-simulation-line" />
          {rows.map((row, index) => {
            const local = ((elapsed - row.offset) % commonCycle + commonCycle) % commonCycle;
            const state = local < row.green ? 'green' : local < row.green + row.yellow ? 'yellow' : 'red';
            return (
              <div
                className="semafix-simulation-signal"
                key={row.id}
                style={{ left: `${(cumulativeDistances[index] / totalDistance) * 100}%` }}
              >
                <span>{row.name}</span>
                <i className={state} />
                <small>{cumulativeDistances[index]} m</small>
              </div>
            );
          })}
        </div>
        <div className="semafix-simulation-legend">
          <span><i className="green" />Verde</span>
          <span><i className="yellow" />Giallo</span>
          <span><i className="red" />Rosso</span>
        </div>
      </Modal>
    </div>
  );
}
